import { streamMessage } from '@/helpers/cortexStream'
import { getToolSchemas, executeTool, getPageContext, getToolCallPolicy, shouldAutoExecuteTool } from '@/helpers/cortexTools'

// Converts any raw error (network, API, provider) into a user-safe message.
// The backend already sanitizes LLM provider errors — this is the last-resort
// client-side guard for network failures and unexpected shapes.
function sanitizeError (err) {
  if (!err) return 'Something went wrong. Please try again.'
  var raw = (err && err.message) || ''
  // The backend sends a pre-sanitized message inside a JSON error body.
  // Try to extract just that message field.
  var jsonStart = raw.indexOf('{"error"')
  if (jsonStart !== -1) {
    try {
      var parsed = JSON.parse(raw.slice(jsonStart))
      var msg = (parsed && parsed.error && parsed.error.message) || (parsed && parsed.message)
      if (msg) return msg
    } catch (e) { /* fall through */ }
  }
  // Network-level errors (fetch failed, connection refused, etc.)
  if (raw.indexOf('Failed to fetch') !== -1 || raw.indexOf('NetworkError') !== -1 || raw.indexOf('network') !== -1) {
    return 'Unable to reach the server. Please check your connection and try again.'
  }
  // Anything else — never show raw internal strings
  return 'Something went wrong. Please try again.'
}

var pendingToolContinuation = null

function parseToolArguments (toolCall) {
  try {
    return JSON.parse((toolCall.function && toolCall.function.arguments) || '{}')
  } catch (e) {
    return {}
  }
}

function stringifyToolArguments (args) {
  try {
    return JSON.stringify(args || {}, null, 2)
  } catch (e) {
    return '{}'
  }
}

function getToolCallName (toolCall) {
  return toolCall && toolCall.function && toolCall.function.name
}

function describeToolCall (toolCall) {
  var args = parseToolArguments(toolCall)
  var policy = getToolCallPolicy(toolCall)
  return {
    id: toolCall.id,
    name: getToolCallName(toolCall),
    arguments: args,
    argumentsText: stringifyToolArguments(args),
    policy: policy,
    toolCall: toolCall
  }
}

function clearPendingToolContinuation (context) {
  pendingToolContinuation = null
  context.commit('SET_PENDING_TOOL_APPROVAL', null)
}

var cortex = {
  namespaced: true,

  state: {
    isOpen: false,
    isSplitMode: localStorage.getItem('kortex_split_mode') === 'true',
    conversations: [],
    activeConversationUUID: null,
    messages: [],
    isStreaming: false,
    streamingContent: '',
    streamingThinking: '',
    isThinking: false,
    streamingToolCalls: [],
    ragSources: [],
    agents: [],
    selectedAgentUUID: 'protokol-bot',
    tier: 'free',
    tierInfo: null,
    knowledgeBases: [],
    abortController: null,
    isOCRLoading: false,
    userAccessList: [],
    myAccess: null,
    isQuickDialogOpen: false,
    pendingToolApproval: null,
    toolAccessMode: 'default',
    allowedSessionTools: [],
  },

  mutations: {
    SET_OPEN: function (state, isOpen) {
      state.isOpen = isOpen
    },
    SET_SPLIT_MODE: function (state, val) {
      state.isSplitMode = val
      localStorage.setItem('kortex_split_mode', val ? 'true' : 'false')
    },
    SET_CONVERSATIONS: function (state, convs) {
      state.conversations = convs
    },
    PREPEND_CONVERSATION: function (state, conv) {
      state.conversations.unshift(conv)
    },
    UPDATE_CONVERSATION: function (state, conv) {
      for (var i = 0; i < state.conversations.length; i++) {
        if (state.conversations[i].uuid === conv.uuid) {
          state.conversations.splice(i, 1, conv)
          break
        }
      }
    },
    REMOVE_CONVERSATION: function (state, uuid) {
      state.conversations = state.conversations.filter(function (c) {
        return c.uuid !== uuid
      })
    },
    ADD_PARTICIPANT: function (state, payload) {
      for (var i = 0; i < state.conversations.length; i++) {
        if (state.conversations[i].uuid === payload.uuid) {
          var conv = state.conversations[i]
          var participants = (conv.participants || []).slice()
          var found = false
          for (var j = 0; j < participants.length; j++) {
            if (participants[j].agent_uuid === payload.participant.agent_uuid) {
              participants[j] = payload.participant
              found = true
              break
            }
          }
          if (!found) {
            participants.push(payload.participant)
          }
          state.conversations.splice(i, 1, Object.assign({}, conv, { participants: participants }))
          break
        }
      }
    },
    REMOVE_PARTICIPANT: function (state, payload) {
      for (var i = 0; i < state.conversations.length; i++) {
        if (state.conversations[i].uuid === payload.uuid) {
          var conv = state.conversations[i]
          var participants = (conv.participants || []).filter(function (p) {
            return p.agent_uuid !== payload.agent_uuid
          })
          state.conversations.splice(i, 1, Object.assign({}, conv, { participants: participants }))
          break
        }
      }
    },
    SET_ACTIVE_CONVERSATION: function (state, uuid) {
      state.activeConversationUUID = uuid
    },
    SET_MESSAGES: function (state, msgs) {
      state.messages = msgs
    },
    ADD_MESSAGE: function (state, msg) {
      state.messages.push(msg)
    },
    REMOVE_LAST_ASSISTANT_MESSAGE: function (state) {
      for (var i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i].role === 'assistant') {
          state.messages.splice(i, 1)
          break
        }
      }
    },
    UPDATE_LAST_ASSISTANT_MESSAGE: function (state, payload) {
      for (var i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i].role === 'assistant') {
          var updated = Object.assign({}, state.messages[i], payload)
          state.messages.splice(i, 1, updated)
          break
        }
      }
    },
    SET_STREAMING: function (state, val) {
      state.isStreaming = val
    },
    APPEND_STREAMING_CONTENT: function (state, delta) {
      state.streamingContent += delta
      // Also update the last assistant message content live
      for (var i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i].role === 'assistant') {
          var updated = Object.assign({}, state.messages[i], { content: state.streamingContent })
          state.messages.splice(i, 1, updated)
          break
        }
      }
    },
    RESET_STREAMING_CONTENT: function (state) {
      state.streamingContent = ''
      state.streamingThinking = ''
      state.isThinking = false
    },
    APPEND_STREAMING_THINKING: function (state, delta) {
      state.streamingThinking += delta
      state.isThinking = true
      // Mirror thinking content into the last assistant message
      for (var i = state.messages.length - 1; i >= 0; i--) {
        if (state.messages[i].role === 'assistant') {
          var updated = Object.assign({}, state.messages[i], { thinking: state.streamingThinking })
          state.messages.splice(i, 1, updated)
          break
        }
      }
    },
    SET_STREAMING_TOOL_CALLS: function (state, calls) {
      state.streamingToolCalls = calls
    },
    SET_RAG_SOURCES: function (state, sources) {
      state.ragSources = sources
    },
    SET_AGENTS: function (state, agents) {
      state.agents = agents
    },
    SET_SELECTED_AGENT: function (state, uuid) {
      state.selectedAgentUUID = uuid
    },
    SET_CONVERSATION_AGENT: function (state, payload) {
      for (var i = 0; i < state.conversations.length; i++) {
        if (state.conversations[i].uuid === payload.uuid) {
          var updated = Object.assign({}, state.conversations[i], { agent_uuid: payload.agentUUID || null })
          state.conversations.splice(i, 1, updated)
          break
        }
      }
    },
    SET_TIER: function (state, tier) {
      state.tier = tier
    },
    SET_TIER_INFO: function (state, info) {
      state.tierInfo = info
    },
    SET_KNOWLEDGE_BASES: function (state, kbs) {
      state.knowledgeBases = kbs
    },
    ADD_KNOWLEDGE_BASE: function (state, kb) {
      state.knowledgeBases.unshift(kb)
    },
    REMOVE_KNOWLEDGE_BASE: function (state, uuid) {
      state.knowledgeBases = state.knowledgeBases.filter(function (kb) {
        return kb.uuid !== uuid
      })
    },
    SET_ABORT_CONTROLLER: function (state, ctrl) {
      state.abortController = ctrl
    },
    SET_OCR_LOADING: function (state, val) {
      state.isOCRLoading = val
    },
    SET_USER_ACCESS_LIST: function (state, list) {
      state.userAccessList = list
    },
    ADD_USER_ACCESS: function (state, ua) {
      state.userAccessList.unshift(ua)
    },
    UPDATE_USER_ACCESS: function (state, ua) {
      for (var i = 0; i < state.userAccessList.length; i++) {
        if (state.userAccessList[i].uuid === ua.uuid) {
          state.userAccessList.splice(i, 1, ua)
          break
        }
      }
    },
    REMOVE_USER_ACCESS: function (state, uuid) {
      state.userAccessList = state.userAccessList.filter(function (ua) {
        return ua.uuid !== uuid
      })
    },
    SET_MY_ACCESS: function (state, ua) {
      state.myAccess = ua
    },
    SET_QUICK_DIALOG_OPEN: function (state, val) {
      state.isQuickDialogOpen = val
    },
    SET_PENDING_TOOL_APPROVAL: function (state, payload) {
      state.pendingToolApproval = payload
    },
    SET_TOOL_ACCESS_MODE: function (state, mode) {
      state.toolAccessMode = mode || 'default'
    },
    ADD_ALLOWED_SESSION_TOOLS: function (state, names) {
      var next = state.allowedSessionTools.slice()
      var list = names || []
      list.forEach(function (name) {
        if (name && next.indexOf(name) === -1) next.push(name)
      })
      state.allowedSessionTools = next
    },
    RESET_TOOL_PERMISSIONS: function (state) {
      state.toolAccessMode = 'default'
      state.allowedSessionTools = []
    },
  },

  actions: {
    openQuickDialog: function (context) {
      context.commit('SET_ACTIVE_CONVERSATION', null)
      context.commit('SET_MESSAGES', [])
      context.commit('SET_QUICK_DIALOG_OPEN', true)
      context.dispatch('loadConversations')
      if (context.state.agents.length === 0) {
        context.dispatch('loadAgents')
      }
      context.dispatch('loadMyAccess')
    },

    closeQuickDialog: function (context) {
      context.commit('SET_QUICK_DIALOG_OPEN', false)
    },

    toggleQuickDialog: function (context) {
      if (context.state.isQuickDialogOpen) {
        context.commit('SET_QUICK_DIALOG_OPEN', false)
      } else {
        context.dispatch('openQuickDialog')
      }
    },

    openChat: function (context) {
      context.commit('SET_OPEN', true)
      context.commit('SET_QUICK_DIALOG_OPEN', false)
      context.dispatch('loadTier')
      context.dispatch('loadAgents')
      context.dispatch('loadMyAccess')
      return context.dispatch('loadConversations')
    },

    closeChat: function (context) {
      context.commit('SET_OPEN', false)
    },

    toggleSplitMode: function (context) {
      var next = !context.state.isSplitMode
      context.commit('SET_SPLIT_MODE', next)
      if (next && !context.state.isOpen) {
        context.commit('SET_OPEN', true)
        context.dispatch('loadConversations')
        context.dispatch('loadAgents')
      }
    },

    loadConversations: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/conversations', {
        params: { limit: 50, page: 1 }
      }).then(function (resp) {
        context.commit('SET_CONVERSATIONS', resp.data.data || [])
        if (resp.data.data && resp.data.data.length > 0 && !context.state.activeConversationUUID && !context.state.isQuickDialogOpen) {
          return context.dispatch('selectConversation', resp.data.data[0].uuid)
        }
      })
    },

    loadTier: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/tier').then(function (resp) {
        context.commit('SET_TIER_INFO', resp.data)
        context.commit('SET_TIER', resp.data.tier || 'free')
      })
    },

    loadAgents: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/agents', {
        params: { limit: 100, page: 1 }
      }).then(function (resp) {
        var agents = resp.data.data || []
        context.commit('SET_AGENTS', agents)
        // If the currently selected agent is not in the available list, reset to protokol-bot
        var selected = context.state.selectedAgentUUID
        if (selected && selected !== 'protokol-bot') {
          var found = agents.find(function (a) { return a.uuid === selected })
          if (!found) {
            context.commit('SET_SELECTED_AGENT', 'protokol-bot')
          }
        }
      })
    },

    createConversation: function (context, agentUUID) {
      clearPendingToolContinuation(context)
      // Abort any in-progress stream from the previous conversation
      if (context.state.abortController) {
        context.state.abortController.abort()
        context.commit('SET_ABORT_CONTROLLER', null)
      }
      context.commit('SET_STREAMING', false)
      context.commit('RESET_STREAMING_CONTENT')
      context.commit('SET_RAG_SOURCES', [])
      context.commit('SET_STREAMING_TOOL_CALLS', [])

      var payload = { title: '' }
      if (agentUUID) payload.agent_uuid = agentUUID
      return this._vm.$http.post('/luma/kortex/v1/conversations', payload).then(function (resp) {
        context.commit('PREPEND_CONVERSATION', resp.data)
        context.commit('SET_ACTIVE_CONVERSATION', resp.data.uuid)
        context.commit('SET_MESSAGES', [])
        context.commit('SET_RAG_SOURCES', [])
        context.commit('RESET_STREAMING_CONTENT')
        return resp.data
      })
    },

    selectConversation: function (context, uuid) {
      clearPendingToolContinuation(context)
      // Abort any in-progress stream from the previous conversation
      if (context.state.abortController) {
        context.state.abortController.abort()
        context.commit('SET_ABORT_CONTROLLER', null)
      }
      context.commit('SET_STREAMING', false)
      context.commit('RESET_STREAMING_CONTENT')
      context.commit('SET_RAG_SOURCES', [])
      context.commit('SET_STREAMING_TOOL_CALLS', [])

      context.commit('SET_ACTIVE_CONVERSATION', uuid)
      context.commit('SET_MESSAGES', [])
      // Restore the agent that was last used in this conversation
      var conv = context.state.conversations.find(function (c) { return c.uuid === uuid })
      context.commit('SET_SELECTED_AGENT', (conv && conv.agent_uuid) || 'protokol-bot')
      return this._vm.$http.get('/luma/kortex/v1/conversations/' + uuid + '/messages', {
        params: { limit: 100, page: 1 }
      }).then(function (resp) {
        context.commit('SET_MESSAGES', resp.data.data || [])
      })
    },

    updateConversationAgent: function (context, payload) {
      // payload: { uuid, agentUUID }
      return this._vm.$http.put('/luma/kortex/v1/conversations/' + payload.uuid, {
        agent_uuid: payload.agentUUID !== null ? payload.agentUUID : null
      }).then(function () {
        context.commit('SET_CONVERSATION_AGENT', { uuid: payload.uuid, agentUUID: payload.agentUUID })
      })
    },

    deleteConversation: function (context, uuid) {
      return this._vm.$http.delete('/luma/kortex/v1/conversations/' + uuid).then(function () {
        context.commit('REMOVE_CONVERSATION', uuid)
        if (context.state.activeConversationUUID === uuid) {
          context.commit('SET_ACTIVE_CONVERSATION', null)
          context.commit('SET_MESSAGES', [])
          var convs = context.state.conversations
          if (convs.length > 0) {
            return context.dispatch('selectConversation', convs[0].uuid)
          }
        }
      })
    },

    archiveConversation: function (context, uuid) {
      return this._vm.$http.post('/luma/kortex/v1/conversations/' + uuid + '/archive').then(function () {
        context.commit('REMOVE_CONVERSATION', uuid)
        if (context.state.activeConversationUUID === uuid) {
          context.commit('SET_ACTIVE_CONVERSATION', null)
          context.commit('SET_MESSAGES', [])
          var convs = context.state.conversations
          if (convs.length > 0) {
            return context.dispatch('selectConversation', convs[0].uuid)
          }
        }
      })
    },

    unarchiveConversation: function (context, uuid) {
      return this._vm.$http.post('/luma/kortex/v1/conversations/' + uuid + '/unarchive')
    },

    renameConversation: function (context, payload) {
      return this._vm.$http.put('/luma/kortex/v1/conversations/' + payload.uuid, { title: payload.title }).then(function (resp) {
        context.commit('UPDATE_CONVERSATION', { uuid: payload.uuid, title: resp.data.title })
      })
    },

    addParticipant: function (context, payload) {
      // payload: { uuid, agent_uuid, name, mode, instructions_override }
      return this._vm.$http.post('/luma/kortex/v1/conversations/' + payload.uuid + '/participants', {
        agent_uuid: payload.agent_uuid,
        name: payload.name,
        mode: payload.mode || 'active',
        instructions_override: payload.instructions_override || ''
      }).then(function (resp) {
        if (resp.data && resp.data.participant) {
          context.commit('ADD_PARTICIPANT', { uuid: payload.uuid, participant: resp.data.participant })
        }
      })
    },

    removeParticipant: function (context, payload) {
      // payload: { uuid, agent_uuid }
      return this._vm.$http.delete('/luma/kortex/v1/conversations/' + payload.uuid + '/participants/' + payload.agent_uuid).then(function () {
        context.commit('REMOVE_PARTICIPANT', payload)
      })
    },

    sendMessage: function (context, payload) {
      if (context.state.pendingToolApproval) return Promise.resolve()
      var content = typeof payload === 'string' ? payload : (payload && payload.content) || ''
      var images = typeof payload === 'string' ? [] : (payload && payload.images) || []
      if ((!content || !content.trim()) && images.length === 0) return Promise.resolve()

      var convUUID = context.state.activeConversationUUID

      var createPromise = convUUID
        ? Promise.resolve()
        : context.dispatch('createConversation', context.state.selectedAgentUUID).then(function (conv) {
            convUUID = conv.uuid
          })

      return createPromise.then(function () {
        context.commit('SET_STREAMING', true)
        context.commit('RESET_STREAMING_CONTENT')
        context.commit('SET_RAG_SOURCES', [])
        context.commit('SET_STREAMING_TOOL_CALLS', [])

        // Optimistic user message
        context.commit('ADD_MESSAGE', {
          uuid: 'temp-user-' + Date.now(),
          role: 'user',
          content: content,
          images: images.length > 0 ? images : undefined,
          created_at: new Date().toISOString()
        })

        // Detect @mention in the content so the placeholder bubble already shows
        // which agent is expected to respond — before message_complete arrives.
        var placeholderAgentUUID = null
        var placeholderAgentName = null
        var activeConv = context.state.conversations.find(function (c) { return c.uuid === convUUID })
        var participants = (activeConv && activeConv.participants) || []
        if (participants.length > 0 && content) {
          var mentionRe = /(?:^|\s)@(\S+)/
          var mentionMatch = content.match(mentionRe)
          if (mentionMatch) {
            var normalizedMention = mentionMatch[1].toLowerCase().replace(/\s/g, '')
            for (var pi = 0; pi < participants.length; pi++) {
              var pNorm = participants[pi].name.toLowerCase().replace(/\s/g, '')
              if (pNorm === normalizedMention) {
                placeholderAgentUUID = participants[pi].agent_uuid
                placeholderAgentName = participants[pi].name
                break
              }
            }
          }
        }

        // Placeholder assistant message
        context.commit('ADD_MESSAGE', {
          uuid: 'temp-assistant-' + Date.now(),
          role: 'assistant',
          content: '',
          tool_calls: [],
          rag_sources: [],
          token_usage: null,
          agent_uuid: placeholderAgentUUID,
          agent_name: placeholderAgentName,
          created_at: new Date().toISOString()
        })

        var ctrl = new AbortController()
        context.commit('SET_ABORT_CONTROLLER', ctrl)

        var tools = getToolSchemas()
        // Only inject page_context on the very first message of a conversation so
        // it lands in the model's context once and is not repeated on every turn.
        var isFirstMessage = context.state.messages.filter(function (m) { return m.role === 'user' }).length <= 1
        var pageContext = isFirstMessage ? getPageContext() : null

        // Inner helper — can be called recursively when the LLM returns tool_calls.
        // First call: sends user content + tool schemas.
        // Subsequent calls: sends empty content + tool_results (LLM continues from there).
        var toolRoundCount = 0
        var MAX_TOOL_ROUNDS = 10
        var aborted = false

        function toolCallsNeedApproval (toolCalls) {
          return toolCalls.some(function (tc) {
            return !shouldAutoExecuteTool(tc, context.state.toolAccessMode, context.state.allowedSessionTools)
          })
        }

        function executeToolCalls (toolCalls, decision) {
          decision = decision || { approved: true, mode: 'auto' }
          return Promise.all(toolCalls.map(function (tc) {
            var funcName = getToolCallName(tc)
            var funcArgs = parseToolArguments(tc)
            var policy = getToolCallPolicy(tc)

            if (decision.approved === false) {
              var denied = {
                error: 'User denied tool execution.',
                denied: true,
                tool: funcName
              }
              var deniedStr = JSON.stringify(denied)
              return {
                toolResult: {
                  tool_call_id: tc.id,
                  name: funcName,
                  content: deniedStr,
                  approval: { approved: false, mode: decision.mode || 'denied' }
                },
                enriched: Object.assign({}, tc, {
                  _result: deniedStr,
                  _approval: { approved: false, mode: decision.mode || 'denied' },
                  _policy: policy
                })
              }
            }

            return executeTool(funcName, funcArgs).then(function (outcome) {
              var resultStr = JSON.stringify(outcome.result !== undefined ? outcome.result : outcome)
              return {
                toolResult: {
                  tool_call_id: tc.id,
                  name: funcName,
                  content: resultStr,
                  approval: { approved: true, mode: decision.mode || 'auto' }
                },
                enriched: Object.assign({}, tc, {
                  _result: resultStr,
                  _approval: { approved: true, mode: decision.mode || 'auto' },
                  _policy: policy
                })
              }
            })
          }))
        }

        function continueWithToolOutcomes (outcomes) {
          var results = outcomes.map(function (o) { return o.toolResult })
          var enrichedToolCalls = outcomes.map(function (o) { return o.enriched })

          // Persist tool calls with results into the current assistant bubble
          context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', { tool_calls: enrichedToolCalls })

          // Create a NEW assistant bubble for the follow-up response
          // (instead of reusing the same one, so each tool-call round
          // appears as a separate message in the chat)
          context.commit('ADD_MESSAGE', {
            uuid: 'temp-assistant-' + Date.now() + '-' + toolRoundCount,
            role: 'assistant',
            content: '',
            thinking: '',
            tool_calls: [],
            rag_sources: [],
            token_usage: null,
            created_at: new Date().toISOString()
          })
          context.commit('RESET_STREAMING_CONTENT')

          // Send tool results back — LLM will produce the final response.
          // Properly chain the recursive promise so abort/errors propagate.
          return runStream('', [], results).catch(function (err) {
            if (err && err.name === 'AbortError') {
              aborted = true
              return
            }
            // Non-abort errors during recursive calls (400, 500, etc.)
            // must be handled here — re-throwing would create an
            // unhandled promise rejection and leave the UI stuck.
            context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
              content: '',
              isError: true,
              errorText: sanitizeError(err)
            })
            context.commit('SET_STREAMING', false)
            context.commit('SET_ABORT_CONTROLLER', null)
          })
        }

        function pauseForToolApproval (toolCalls) {
          context.commit('SET_PENDING_TOOL_APPROVAL', {
            calls: toolCalls.map(describeToolCall),
            accessMode: context.state.toolAccessMode,
            createdAt: new Date().toISOString()
          })

          pendingToolContinuation = function (decision) {
            return executeToolCalls(toolCalls, decision).then(continueWithToolOutcomes)
          }
        }

        function runStream (streamContent, streamImages, toolResults) {
          // Guard: don't start a new stream if already aborted or max rounds reached
          if (aborted) return Promise.resolve()
          if (toolResults && toolResults.length > 0) {
            toolRoundCount++
            if (toolRoundCount > MAX_TOOL_ROUNDS) {
              context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
                content: context.state.streamingContent + '\n\n⚠️ Maximum tool call rounds reached — the agent was stopped automatically.'
              })
              context.commit('SET_STREAMING', false)
              context.commit('SET_ABORT_CONTROLLER', null)
              return Promise.resolve()
            }
          }

          // Track which agent is currently responding (from message_complete events)
          // so tool-result follow-ups use the same agent, not the UI-selected one.
          var respondingAgentUUID = context.state.selectedAgentUUID
          var currentToolCalls = null

          return streamMessage(
            convUUID,
            streamContent,
            respondingAgentUUID,
            ctrl.signal,
            function (event) {
              // Guard: stop processing events after abort or conversation switch
              if (aborted) return
              if (context.state.activeConversationUUID !== convUUID) return

              if (event.type === 'error' && event.data) {
                // Server-side error during streaming — message is already sanitized by the backend
                var errMsg = (event.data && event.data.error) || 'Something went wrong. Please try again.'
                context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
                  content: '',
                  isError: true,
                  errorText: errMsg
                })
                context.commit('SET_STREAMING', false)
                context.commit('SET_ABORT_CONTROLLER', null)
                aborted = true
                return
              }

              if (event.type === 'thinking_delta' && event.data) {
                context.commit('APPEND_STREAMING_THINKING', event.data.delta || '')
              } else if (event.type === 'content_delta' && event.data) {
                context.state.isThinking = false
                context.commit('APPEND_STREAMING_CONTENT', event.data.delta || '')
              } else if (event.type === 'rag_sources' && event.data) {
                context.commit('SET_RAG_SOURCES', event.data.sources || [])
                context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
                  rag_sources: event.data.sources || []
                })
              } else if (event.type === 'message_complete' && event.data) {
                currentToolCalls = event.data.tool_calls || []

                // Lock onto the agent that actually responded (from @mention or active routing)
                if (event.data.agent_uuid) {
                  respondingAgentUUID = event.data.agent_uuid
                }

                var finalContent = event.data.content || context.state.streamingContent

                // If a chained agent produced no content (even if it had thinking),
                // remove the placeholder bubble — only agents that actually said
                // something should appear in the chat.
                if (!finalContent && event.data.agent_uuid && event.data.agent_uuid !== (context.state.selectedAgentUUID || null)) {
                  context.commit('REMOVE_LAST_ASSISTANT_MESSAGE')
                  context.commit('RESET_STREAMING_CONTENT')
                  return
                }

                var updatePayload = {
                  uuid: event.data.uuid || null,
                  content: finalContent,
                  token_usage: event.data.token_usage || null,
                  rag_sources: event.data.rag_sources || context.state.ragSources,
                  agent_uuid: event.data.agent_uuid || null,
                  agent_name: event.data.agent_name || null
                }
                // Only overwrite tool_calls when non-empty (preserves them in the second round)
                if (currentToolCalls.length > 0) {
                  updatePayload.tool_calls = currentToolCalls
                }
                context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', updatePayload)

                if (event.data.conversation_title) {
                  context.commit('UPDATE_CONVERSATION', {
                    uuid: convUUID,
                    title: event.data.conversation_title
                  })
                }

                // Execute any frontend tool calls and continue the conversation
                if (currentToolCalls.length > 0) {
                  if (toolCallsNeedApproval(currentToolCalls)) {
                    pauseForToolApproval(currentToolCalls)
                    return
                  }
                  var mode = context.state.toolAccessMode === 'full' ? 'full_auto' : 'auto'
                  return executeToolCalls(currentToolCalls, { approved: true, mode: mode }).then(continueWithToolOutcomes)
                }
              } else if (event.type === 'agent_switch' && event.data) {
                // A chained agent is about to respond — open a fresh bubble for it.
                // (message_complete for the previous agent already fired, so that
                //  bubble is finalized. We just need a new placeholder.)
                context.commit('RESET_STREAMING_CONTENT')
                context.commit('ADD_MESSAGE', {
                  uuid: 'temp-assistant-chain-' + Date.now(),
                  role: 'assistant',
                  content: '',
                  thinking: '',
                  tool_calls: [],
                  rag_sources: [],
                  token_usage: null,
                  agent_uuid: event.data.agent_uuid || null,
                  agent_name: event.data.agent_name || null,
                  created_at: new Date().toISOString()
                })
                if (event.data.agent_uuid) {
                  respondingAgentUUID = event.data.agent_uuid
                }
              } else if (event.type === 'done') {
                // Only stop streaming if no tool calls are pending (recursive runStream handles its own done)
                if (!(currentToolCalls && currentToolCalls.length > 0)) {
                  context.commit('SET_STREAMING', false)
                  context.commit('SET_ABORT_CONTROLLER', null)
                }
              }
            },
            streamImages,
            tools,
            toolResults || [],
            toolResults && toolResults.length > 0 ? null : pageContext
          )
        }

        return runStream(content, images, []).catch(function (err) {
          if (err && err.name === 'AbortError') return
          context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
            content: '',
            isError: true,
            errorText: sanitizeError(err)
          })
          context.commit('SET_STREAMING', false)
          context.commit('SET_ABORT_CONTROLLER', null)
        })
      })
    },

    stopGeneration: function (context) {
      clearPendingToolContinuation(context)
      if (context.state.abortController) {
        context.state.abortController.abort()
        context.commit('SET_STREAMING', false)
        context.commit('SET_ABORT_CONTROLLER', null)
      }
    },

    approvePendingTools: function (context, payload) {
      if (!pendingToolContinuation) return Promise.resolve()
      var mode = (payload && payload.mode) || 'allow_once'
      if (mode === 'full') {
        context.commit('SET_TOOL_ACCESS_MODE', 'full')
      } else if (mode === 'allow_tool') {
        context.commit('ADD_ALLOWED_SESSION_TOOLS', payload && payload.toolNames)
      }
      var continuation = pendingToolContinuation
      pendingToolContinuation = null
      context.commit('SET_PENDING_TOOL_APPROVAL', null)
      return continuation({ approved: true, mode: mode }).catch(function (err) {
        context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
          content: '',
          isError: true,
          errorText: sanitizeError(err)
        })
        context.commit('SET_STREAMING', false)
        context.commit('SET_ABORT_CONTROLLER', null)
      })
    },

    denyPendingTools: function (context) {
      if (!pendingToolContinuation) return Promise.resolve()
      var continuation = pendingToolContinuation
      pendingToolContinuation = null
      context.commit('SET_PENDING_TOOL_APPROVAL', null)
      return continuation({ approved: false, mode: 'denied' }).catch(function (err) {
        context.commit('UPDATE_LAST_ASSISTANT_MESSAGE', {
          content: '',
          isError: true,
          errorText: sanitizeError(err)
        })
        context.commit('SET_STREAMING', false)
        context.commit('SET_ABORT_CONTROLLER', null)
      })
    },

    resetToolAccessMode: function (context) {
      context.commit('RESET_TOOL_PERMISSIONS')
    },

    // Knowledge Bases
    loadKnowledgeBases: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/knowledge-bases', {
        params: { limit: 100, page: 1 }
      }).then(function (resp) {
        context.commit('SET_KNOWLEDGE_BASES', resp.data.data || [])
        return resp.data.data || []
      })
    },

    createKnowledgeBase: function (context, payload) {
      return this._vm.$http.post('/luma/kortex/v1/knowledge-bases', payload).then(function (resp) {
        context.commit('ADD_KNOWLEDGE_BASE', resp.data)
        return resp.data
      })
    },

    deleteKnowledgeBase: function (context, uuid) {
      return this._vm.$http.delete('/luma/kortex/v1/knowledge-bases/' + uuid).then(function () {
        context.commit('REMOVE_KNOWLEDGE_BASE', uuid)
      })
    },

    updateKnowledgeBase: function (context, payload) {
      return this._vm.$http.patch('/luma/kortex/v1/knowledge-bases/' + payload.uuid, payload).then(function (resp) {
        return resp.data
      })
    },

    // Agents
    createAgent: function (context, payload) {
      return this._vm.$http.post('/luma/kortex/v1/agents', payload).then(function (resp) {
        context.commit('SET_AGENTS', context.state.agents.concat([resp.data]))
        return resp.data
      })
    },

    updateAgent: function (context, payload) {
      return this._vm.$http.patch('/luma/kortex/v1/agents/' + payload.uuid, payload).then(function (resp) {
        var updated = resp.data
        var agents = context.state.agents.map(function (a) {
          return a.uuid === updated.uuid ? updated : a
        })
        context.commit('SET_AGENTS', agents)
        return updated
      })
    },

    deleteAgent: function (context, uuid) {
      return this._vm.$http.delete('/luma/kortex/v1/agents/' + uuid).then(function () {
        var agents = context.state.agents.filter(function (a) {
          return a.uuid !== uuid
        })
        context.commit('SET_AGENTS', agents)
      })
    },

    // Returns the full KnowledgeBase objects integrated with a specific agent.
    loadIntegratedKBs: function (context, agentUUID) {
      return this._vm.$http.get('/luma/kortex/v1/agents/' + agentUUID + '/knowledge-bases').then(function (resp) {
        return resp.data || []
      }).catch(function () {
        return []
      })
    },

    performOCR: function (context, payload) {
      // payload: { fileName, fileData (base64 data URI), contentType }
      context.commit('SET_OCR_LOADING', true)
      var vm = this._vm
      return vm.$http.post('/luma/kortex/v1/ocr', {
        file_name: payload.fileName,
        file_data: payload.fileData,
        content_type: payload.contentType
      }).then(function (resp) {
        return resp.data
      }).finally(function () {
        context.commit('SET_OCR_LOADING', false)
      })
    },

    injectOCRResult: function (context, payload) {
      // payload: { fileName, fileData, contentType }
      var vm = this._vm
      var fileName = payload.fileName

      // Show user bubble immediately so the chat feels responsive
      context.commit('ADD_MESSAGE', {
        uuid: 'ocr-user-' + Date.now(),
        role: 'user',
        content: '[OCR] ' + fileName,
        created_at: new Date().toISOString()
      })

      // Typing indicator now shows in the messages area (isOCRLoading drives it)
      context.commit('SET_OCR_LOADING', true)

      return vm.$http.post('/luma/kortex/v1/ocr', {
        file_name: fileName,
        file_data: payload.fileData,
        content_type: payload.contentType
      }, { timeout: 120000 }).then(function (resp) {
        var text = resp.data.text

        // Show assistant bubble with the extracted text
        context.commit('ADD_MESSAGE', {
          uuid: 'ocr-assistant-' + Date.now(),
          role: 'assistant',
          content: text,
          tool_calls: [],
          rag_sources: [],
          token_usage: null,
          created_at: new Date().toISOString()
        })

        // Persist both messages to the backend conversation
        var convUUID = context.state.activeConversationUUID
        var createPromise = convUUID
          ? Promise.resolve()
          : context.dispatch('createConversation', context.state.selectedAgentUUID).then(function (conv) {
              convUUID = conv.uuid
            })

        return createPromise.then(function () {
          return vm.$http.post('/luma/kortex/v1/conversations/' + convUUID + '/messages/inject', {
            role: 'user',
            content: '[OCR] ' + fileName
          }).then(function () {
            return vm.$http.post('/luma/kortex/v1/conversations/' + convUUID + '/messages/inject', {
              role: 'assistant',
              content: text
            })
          })
        }).catch(function () {
          // Saving to backend failed — messages are still shown locally.
        })
      }).finally(function () {
        context.commit('SET_OCR_LOADING', false)
      })
    },

    // User Access (admin)
    loadUserAccessList: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/user-access', {
        params: { limit: 100, page: 1 }
      }).then(function (resp) {
        context.commit('SET_USER_ACCESS_LIST', resp.data.data || [])
        return resp.data.data || []
      })
    },

    createUserAccess: function (context, payload) {
      return this._vm.$http.post('/luma/kortex/v1/user-access', payload).then(function (resp) {
        context.commit('ADD_USER_ACCESS', resp.data)
        return resp.data
      })
    },

    updateUserAccess: function (context, payload) {
      return this._vm.$http.patch('/luma/kortex/v1/user-access/' + payload.uuid, payload).then(function (resp) {
        context.commit('UPDATE_USER_ACCESS', resp.data)
        return resp.data
      })
    },

    deleteUserAccess: function (context, uuid) {
      return this._vm.$http.delete('/luma/kortex/v1/user-access/' + uuid).then(function () {
        context.commit('REMOVE_USER_ACCESS', uuid)
      })
    },

    topUpCredits: function (context, payload) {
      return this._vm.$http.post('/luma/kortex/v1/user-access/' + payload.uuid + '/top-up', {
        amount: payload.amount,
        reason: payload.reason || 'admin_topup'
      }).then(function (resp) {
        return resp.data
      })
    },

    loadMyAccess: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/my-access').then(function (resp) {
        context.commit('SET_MY_ACCESS', resp.data)
        return resp.data
      }).catch(function () {
        context.commit('SET_MY_ACCESS', null)
        return null
      })
    },

    // Admin conversations list (all users)
    loadAllConversations: function (context) {
      return this._vm.$http.get('/luma/kortex/v1/conversations', {
        params: { limit: 50, page: 1, all_users: true }
      }).then(function (resp) {
        context.commit('SET_CONVERSATIONS', resp.data.data || [])
        return resp.data.data || []
      })
    },

  },

  getters: {
    isOpen: function (state) { return state.isOpen },
    isSplitMode: function (state) { return state.isSplitMode },
    conversations: function (state) { return state.conversations },
    activeConversationUUID: function (state) { return state.activeConversationUUID },
    messages: function (state) { return state.messages },
    isStreaming: function (state) { return state.isStreaming },
    isThinking: function (state) { return state.isThinking },
    streamingThinking: function (state) { return state.streamingThinking },
    ragSources: function (state) { return state.ragSources },
    agents: function (state) { return state.agents },
    selectedAgentUUID: function (state) { return state.selectedAgentUUID },
    tier: function (state) { return state.tier },
    tierInfo: function (state) { return state.tierInfo },
    canCreateAgents: function (state) {
      if (state.tierInfo === null) { return false }
      return state.tierInfo.can_create_agents
    },
    canCreateKBs: function (state) {
      if (state.tierInfo === null) { return false }
      return state.tierInfo.can_create_kbs
    },
    canAssignUsers: function (state) {
      if (state.tierInfo === null) { return false }
      return state.tierInfo.can_assign_users
    },
    knowledgeBases: function (state) { return state.knowledgeBases },
    tierVariant: function (state) {
      if (state.tier === 'pro') return 'warning'
      if (state.tier === 'standard') return 'info'
      return 'secondary'
    },
    userAccessList: function (state) { return state.userAccessList },
    myAccess: function (state) { return state.myAccess },
    pendingToolApproval: function (state) { return state.pendingToolApproval },
    toolAccessMode: function (state) { return state.toolAccessMode },
    allowedSessionTools: function (state) { return state.allowedSessionTools },
    // true when the current non-admin user has been explicitly granted Kortex access.
    // Admin access is evaluated separately in components via $isAdmin().
    hasKortexUserAccess: function (state) { return state.myAccess !== null },
    myCreditsRemaining: function (state) {
      if (!state.myAccess) return 0
      return Math.max(0, (state.myAccess.credit_limit || 0) - (state.myAccess.credits_used || 0))
    },
  }
}

export default cortex
